import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";

/**
 * PROTOTYPE: edits made on the canvas. They are replayed on a copy of the API tree and are never sent to the
 * backend (reload = undo all).
 */
export type TaxonomyEdit =
    | {type: 'rename', id: number, label: string}
    | {type: 'delete', id: number}
    /**
     * `sourceId` is removed; its sub-categories and spend sources go to `targetId`, which gets `name` (if given).
     * Like the backend's merge detection: the result is the category with more than one `sources` id.
     */
    | {type: 'merge', sourceId: number, targetId: number, name?: string}
    /** `id` (with its branch) becomes a child of `parentId` */
    | {type: 'move', id: number, parentId: number}

/** Who made an edit (a snapshot, so it shows even when the user list is not loaded) */
export type EditAuthor = {id?: number, name: string}

/** An edit as stored: `key` identifies it so a single change can be accepted or reverted */
export type KeyedEdit = TaxonomyEdit & {key: number, author: EditAuthor}

/** Number of (non-removed) categories below this one, at any depth */
export function countDescendants(n: m_taxonomy.Tree): number {
    return n.children.reduce((sum, c) => c.removed ? sum : sum + 1 + countDescendants(c), 0);
}

function cloneTree(n: m_taxonomy.Tree): m_taxonomy.Tree {
    return {...n, sources: [...n.sources], children: n.children.map(cloneTree)};
}

/**
 * Returns a copy of the tree with the edits applied in order; edits on unknown ids are skipped.
 * Deleted and merged-away categories stay in the tree with `removed: true` (like the API's diff tree).
 */
export function applyEdits(root: m_taxonomy.Tree, edits: readonly TaxonomyEdit[]): m_taxonomy.Tree {
    if (edits.length === 0) return root;
    const copy = cloneTree(root);

    const byId = new Map<number, m_taxonomy.Tree>();
    const parentOf = new Map<number, m_taxonomy.Tree>();
    const register = (n: m_taxonomy.Tree) => {
        if (n.removed) return;
        byId.set(n.id, n);
        n.children.forEach(c => {
            parentOf.set(c.id, n);
            register(c);
        });
    };
    register(copy);

    const detach = (n: m_taxonomy.Tree) => {
        const parent = parentOf.get(n.id);
        if (parent) parent.children = parent.children.filter(c => c !== n);
    };
    const attach = (n: m_taxonomy.Tree, parent: m_taxonomy.Tree) => {
        parent.children = [...parent.children, n];
        parentOf.set(n.id, parent);
    };
    /** Removes the category and everything below it from further edits */
    const forget = (n: m_taxonomy.Tree) => {
        byId.delete(n.id);
        n.children.forEach(forget);
    };

    for (const edit of edits) {
        switch (edit.type) {
            case 'rename': {
                const n = byId.get(edit.id);
                if (n) n.label = edit.label;
                break;
            }
            case 'delete': {
                const n = byId.get(edit.id);
                if (!n || n === copy) break;
                n.removed = true;
                forget(n);
                break;
            }
            case 'merge': {
                const source = byId.get(edit.sourceId);
                const target = byId.get(edit.targetId);
                if (!source || !target || source === target || source === copy) break;
                source.children.filter(c => !c.removed).forEach(c => attach(c, target));
                source.children = source.children.filter(c => c.removed);
                target.sources = [...target.sources, ...source.sources];
                if (edit.name) target.label = edit.name;
                source.removed = true;
                forget(source);
                break;
            }
            case 'move': {
                const n = byId.get(edit.id);
                const parent = byId.get(edit.parentId);
                if (!n || !parent || n === copy || parentOf.get(n.id) === parent) break;
                detach(n);
                attach(n, parent);
                break;
            }
        }
    }
    return copy;
}
